import axios from 'axios';
import useAuthStore from '../store/authStore';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:4000/api',
  withCredentials: true,
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().clearUser();
    }
    return Promise.reject(error);
  }
);

/**
 * Get ebooks by language with custom sorting
 * 
 * @param {string} language - Language to filter by (e.g., 'english', 'spanish')
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 10)
 * @param {string} options.sort - Sort order ('latest', 'trending', 'popular') (default: 'latest')
 * @param {boolean} options.uniqueSeries - Whether to return only one book per series (default: false)
 * @returns {Promise<Object>} Response data with ebooks and pagination info
 */
export const getEbooksByLanguage = async (language, {
  page = 1,
  limit = 10,
  sort = 'latest',
  uniqueSeries = false
} = {}) => {
  const response = await api.get('/ebooks/by-language', {
    params: {
      language,
      page,
      limit,
      sort,
      uniqueSeries
    }
  });
  return response.data;
};

/**
 * Get trending ebooks by language
 * 
 * @param {string} language - Language to filter by
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 10)
 * @param {boolean} options.uniqueSeries - Whether to return only one book per series (default: false)
 * @returns {Promise<Object>} Response data with ebooks and pagination info
 */
export const getTrendingEbooksByLanguage = async (language, {
  page = 1,
  limit = 10,
  uniqueSeries = false
} = {}) => {
  const response = await api.get('/ebooks/by-language/trending', {
    params: {
      language,
      page,
      limit,
      uniqueSeries
    }
  });
  return response.data;
};

/**
 * Get latest ebooks by language
 * 
 * @param {string} language - Language to filter by
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 10)
 * @param {boolean} options.uniqueSeries - Whether to return only one book per series (default: false)
 * @returns {Promise<Object>} Response data with ebooks and pagination info
 */
export const getLatestEbooksByLanguage = async (language, {
  page = 1,
  limit = 10,
  uniqueSeries = false
} = {}) => {
  const response = await api.get('/ebooks/by-language/latest', {
    params: {
      language,
      page,
      limit,
      uniqueSeries
    }
  });
  return response.data;
};

/**
 * Get popular (most liked) ebooks by language
 * 
 * @param {string} language - Language to filter by
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 10)
 * @param {boolean} options.uniqueSeries - Whether to return only one book per series (default: false)
 * @returns {Promise<Object>} Response data with ebooks and pagination info
 */
export const getPopularEbooksByLanguage = async (language, {
  page = 1,
  limit = 10,
  uniqueSeries = false
} = {}) => {
  const response = await api.get('/ebooks/by-language/popular', {
    params: {
      language,
      page,
      limit,
      uniqueSeries
    }
  });
  return response.data;
};